import type { AcceptancePolicyReason } from "@/domain/order/acceptance-policy.types"
import {
  isRegisteredAcceptanceReason,
  resolveRegisteredReasonOrUnknown,
} from "@/domain/order/acceptance-policy-registry"

export type AcceptancePolicyEnvelope = {
  resultCode?: unknown
  success?: boolean
  message?: unknown
  reason?: unknown
  /** Backend `ApiResponse.code` (e.g. DESTINATION_LOCATION_REQUIRED on HTTP 400). */
  code?: unknown
  data?: unknown
}

/** Coerce number or numeric string once. */
export function normalizeResultCode(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value.trim())
    return Number.isFinite(n) ? n : null
  }
  return null
}

function reasonFromUnknown(value: unknown): string | null {
  if (value == null) return null
  const s = String(value).trim().toUpperCase()
  return s || null
}

/** Top-level reason, then nested data.reason, then ApiResponse.code. */
export function extractAcceptancePolicyReason(
  envelope: AcceptancePolicyEnvelope | undefined | null
): string | null {
  if (!envelope) return null
  const top = reasonFromUnknown(envelope.reason)
  if (top) return top
  if (envelope.data && typeof envelope.data === "object") {
    const nested = reasonFromUnknown((envelope.data as { reason?: unknown }).reason)
    if (nested) return nested
  }
  return reasonFromUnknown(envelope.code)
}

export function resultCodeToAcceptanceReason(
  resultCode: number | null
): AcceptancePolicyReason | null {
  if (resultCode === 4091) return "ACTIVE_UNPAID_ORDER_EXISTS"
  if (resultCode === 4092) return "ACTIVE_ORDER_LIMIT_REACHED"
  if (resultCode === 4093) return "RESTAURANT_UNAVAILABLE"
  if (resultCode === 4094) return "RESTAURANT_CLOSED"
  if (resultCode === 4095) return "DESTINATION_NOT_SERVICEABLE"
  if (resultCode === 4096) return "SERVICEABILITY_UNAVAILABLE"
  if (resultCode === 4097) return "ITEM_NOT_AVAILABLE"
  if (resultCode === 4098) return "ORDER_VALIDATION_FAILED"
  if (resultCode === 4099) return "PICKUP_NOT_AVAILABLE"
  return null
}

/**
 * True when envelope signals an order-acceptance policy (reason or known code).
 * Does not require HTTP 409.
 */
export function isAcceptancePolicyEnvelope(
  envelope: AcceptancePolicyEnvelope | undefined | null
): boolean {
  if (!envelope) return false
  const reason = extractAcceptancePolicyReason(envelope)
  if (reason && isRegisteredAcceptanceReason(reason)) return true
  const code = normalizeResultCode(envelope.resultCode)
  return resultCodeToAcceptanceReason(code) != null
}

/**
 * Resolve stable AcceptancePolicyReason from envelope.
 * Stubbed future reasons (resolveAsSelf: false) become UNKNOWN until activated.
 */
export function resolveAcceptancePolicyReason(
  envelope: AcceptancePolicyEnvelope
): AcceptancePolicyReason {
  const raw = extractAcceptancePolicyReason(envelope)
  if (raw) return resolveRegisteredReasonOrUnknown(raw)

  const fromCode = resultCodeToAcceptanceReason(normalizeResultCode(envelope.resultCode))
  if (fromCode) return fromCode
  return "UNKNOWN_ACCEPTANCE_POLICY"
}

export function parseAcceptancePolicyMessage(message: unknown): string | null {
  if (message == null) return null
  if (typeof message === "string") {
    try {
      const parsed = JSON.parse(message) as Array<{ error_message?: string }>
      if (Array.isArray(parsed) && parsed[0]?.error_message) return parsed[0].error_message
    } catch {
      return message
    }
    return message
  }
  if (Array.isArray(message) && message[0] && typeof message[0] === "object") {
    const first = message[0] as { error_message?: string }
    if (first.error_message) return first.error_message
  }
  return null
}
